import type {
  OpenImageViewerConfig,
  OpenImageViewerCopy,
  OpenImageViewerLoadedImage,
} from './types'

const BYTES_IN_KB = 1024
const BYTES_IN_MB = BYTES_IN_KB * 1024

export class OpenImageViewerError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message)
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < BYTES_IN_KB) return `${bytes} B`
  if (bytes < BYTES_IN_MB) return `${(bytes / BYTES_IN_KB).toFixed(1)} KB`
  return `${(bytes / BYTES_IN_MB).toFixed(2)} MB`
}

export function validateOpenImageFile(
  file: File,
  config: OpenImageViewerConfig,
  copy: OpenImageViewerCopy,
): OpenImageViewerError | null {
  if (!isAcceptedFile(file, config)) {
    return new OpenImageViewerError(copy.errorsText.unsupported, 'unsupported')
  }

  if (file.size > config.maxFileSizeBytes) {
    return new OpenImageViewerError(copy.errorsText.too_large, 'too_large')
  }

  return null
}

export function isAcceptedFile(file: File, config: OpenImageViewerConfig): boolean {
  const mime = file.type.toLowerCase()
  const name = file.name.toLowerCase()

  return (
    config.acceptedMimeTypes.includes(mime) ||
    config.acceptedExtensions.some((extension) => name.endsWith(extension))
  )
}

export function loadNativeImage(
  file: File,
  config: OpenImageViewerConfig,
  copy: OpenImageViewerCopy,
  signal?: AbortSignal,
): Promise<OpenImageViewerLoadedImage> {
  if (signal?.aborted) return Promise.reject(createAbortError())
  const previewUrl = URL.createObjectURL(file)

  return new Promise((resolve, reject) => {
    const image = new Image()
    const cleanup = (): void => {
      image.onload = null
      image.onerror = null
      signal?.removeEventListener('abort', abort)
    }
    const abort = (): void => {
      cleanup()
      image.src = ''
      URL.revokeObjectURL(previewUrl)
      reject(new DOMException('Image loading was aborted', 'AbortError'))
    }
    if (signal?.aborted) {
      abort()
      return
    }
    signal?.addEventListener('abort', abort, { once: true })
    image.onload = () => {
      cleanup()
      const width = image.naturalWidth
      const height = image.naturalHeight

      if (!width || !height) {
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(new OpenImageViewerError(copy.errorsText.corrupt, 'corrupt'))
        return
      }

      if (config.maxPixels && width * height > config.maxPixels) {
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(
          new OpenImageViewerError(
            copy.errorsText.too_large_image,
            'too_large_image',
          ),
        )
        return
      }

      // The shell renders its own preview image from previewUrl. Do not retain
      // this loader's decoded native image after reading its dimensions.
      image.src = ''
      resolve({
        previewUrl,
        width,
        height,
        mimeLabel: file.type || 'application/octet-stream',
        formatLabel: config.formatLabel,
        revoke: () => URL.revokeObjectURL(previewUrl),
      })
    }
    image.onerror = () => {
      cleanup()
      image.src = ''
      URL.revokeObjectURL(previewUrl)
      reject(
        new OpenImageViewerError(
          copy.errorsText.decode_failure,
          'decode_failure',
        ),
      )
    }
    image.src = previewUrl
  })
}

function createAbortError(): DOMException {
  return new DOMException('Operation aborted', 'AbortError')
}
